import { useId, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';
import { Banner, Button, Checkbox, Dialog, TextField, useSnackbar } from '@meshble/molo';
import { api, must, type Instance } from '../lib/api';
import { Anello } from '../brand';
import { t } from '../lib/lingua';
import { ConPermesso } from '../permesso';
import { usePermessiSu } from '../session';

/**
 * Quello che si dichiara dal pannello e la riconfigurazione applica: le
 * librerie Python dell'istanza e i moduli da installare o aggiornare dentro
 * Odoo. Non c'è un terminale, e non manca: sono operazioni con parametri
 * controllati, eseguite come job con il loro log e la loro riga nel registro.
 */

/** La forma di una libreria pip, la stessa che l'hub accetta: si dice subito, non dopo il clic. */
const LIBRERIA = /^[A-Za-z0-9][A-Za-z0-9._-]*(\[[A-Za-z0-9_.,-]+\])?((==|>=|<=|~=|!=|<|>)[A-Za-z0-9.*+!-]+(,(==|>=|<=|~=|!=|<|>)[A-Za-z0-9.*+!-]+)*)?$/;
const MODULO = /^[A-Za-z0-9_]+$/;

/** Una per riga, o separate da spazi e virgole: come le scrive chi le copia da un README. */
export function elenco(testo: string): string[] {
  return [...new Set(testo.split(/[\s,]+/).map((v) => v.trim()).filter(Boolean))];
}

/**
 * «Librerie Python»: la lista è parte dell'istanza — una copia ripristinata
 * o di prova nasce con le stesse — e «Applica» la mette nel venv e riavvia.
 */
export function LibreriePython({ slug, istanza }: { slug: string; istanza: Instance }) {
  const permessi = usePermessiSu(slug, { istanza: istanza.id });
  const qc = useQueryClient();
  const navigate = useNavigate();
  const avvisa = useSnackbar();
  const campoId = useId();
  const [testo, setTesto] = useState((istanza.pythonPackages ?? []).join('\n'));
  const voci = elenco(testo);
  const sbagliate = voci.filter((v) => !LIBRERIA.test(v));
  const cambiata = voci.join(' ') !== (istanza.pythonPackages ?? []).join(' ');

  const applica = useMutation({
    mutationFn: () =>
      must(
        api.PUT('/api/v1/orgs/{slug}/instances/{id}/python-packages', {
          params: { path: { slug, id: istanza.id } },
          body: { packages: voci },
        }),
      ),
    onSuccess: (r) => {
      qc.invalidateQueries({ queryKey: ['instance', slug, istanza.id] });
      avvisa(t('Installo le librerie e riavvio l’istanza'));
      if (r.jobId) navigate({ to: '/orgs/$slug/jobs/$id', params: { slug, id: r.jobId } });
    },
  });

  return (
    <div className="md-stack">
      <p className="md-body-medium" style={{ margin: 0, color: 'var(--md-sys-color-on-surface-variant)' }}>
        {t('Le librerie pip che i moduli di questa istanza chiedono e che Odoo non porta. Vanno nel suo ambiente Python, e una copia ripristinata o di prova nasce con le stesse. Quelle dichiarate nel {file} di un repository collegato si installano già da sole. Toglierne una non la disinstalla da qui: sparisce dalle copie future.', {
          file: <code className="md-mono">requirements.txt</code>,
        })}
      </p>
      <div className={`md-field${sbagliate.length > 0 ? ' md-field--error' : ''}`}>
        <label htmlFor={campoId}>{t('Librerie, una per riga')}</label>
        <textarea
          id={campoId}
          rows={4}
          spellCheck={false}
          placeholder={'python-barcode==0.15.1\npandas'}
          value={testo}
          onChange={(e) => setTesto(e.target.value)}
        />
        <span className="md-field__help">
          {sbagliate.length > 0
            ? t('Non va: {voci}. Si scrive nome, nome==versione o nome[extra]>=versione: niente indirizzi, niente opzioni.', { voci: sbagliate.join(', ') })
            : t('Come le vuole pip: nome, nome==versione, nome[extra]>=versione. Meglio con la versione, così la copia è uguale.')}
        </span>
      </div>
      {applica.error && <Banner tone="error" title={t('Non applicate')}>{(applica.error as Error).message}</Banner>}
      <div className="md-stack__inline">
        <ConPermesso ha={permessi} serve="instance.deploy">
          <Button
            variant="tonal"
            icon={applica.isPending ? <Anello /> : undefined}
            disabled={!cambiata || sbagliate.length > 0 || applica.isPending || istanza.status !== 'running'}
            onClick={() => applica.mutate()}
          >
            {applica.isPending ? t('Avvio…') : t('Applica e riavvia')}
          </Button>
        </ConPermesso>
      </div>
    </div>
  );
}

/**
 * «Moduli…»: installare o aggiornare moduli dentro Odoo, o aggiornarli
 * tutti. Ferma l'istanza per il tempo che serve, con una copia del database
 * prima: la stessa strada dell'attivazione di Enterprise.
 */
export function ModuliOdoo({ slug, istanza }: { slug: string; istanza: Instance }) {
  const permessi = usePermessiSu(slug, { istanza: istanza.id });
  const qc = useQueryClient();
  const navigate = useNavigate();
  const avvisa = useSnackbar();
  const [apri, setApri] = useState(false);
  const [installa, setInstalla] = useState('');
  const [aggiorna, setAggiorna] = useState('');
  const [tutti, setTutti] = useState(false);

  // I moduli che stanno nei repository collegati e che Odoo non ha in
  // elenco. Si chiedono solo a dialogo aperto: è una domanda che va sul
  // server, e la risposta serve solo a chi sta per installare qualcosa.
  const nuovi = useQuery({
    queryKey: ['moduli-nuovi', slug, istanza.id],
    enabled: apri && istanza.status === 'running',
    queryFn: () =>
      must(api.GET('/api/v1/orgs/{slug}/instances/{id}/modules/new', {
        params: { path: { slug, id: istanza.id } },
      })),
  });

  const daInstallare = elenco(installa);
  const daAggiornare = tutti ? ['all'] : elenco(aggiorna);
  const sbagliati = [...daInstallare, ...daAggiornare].filter((m) => !MODULO.test(m));
  const pronto = (daInstallare.length > 0 || daAggiornare.length > 0) && sbagliati.length === 0;

  const esegui = useMutation({
    mutationFn: () =>
      must(
        api.POST('/api/v1/orgs/{slug}/instances/{id}/modules', {
          params: { path: { slug, id: istanza.id } },
          body: { install: daInstallare, update: daAggiornare },
        }),
      ),
    onSuccess: (r) => {
      qc.invalidateQueries({ queryKey: ['instance', slug, istanza.id] });
      setApri(false);
      setInstalla('');
      setAggiorna('');
      setTutti(false);
      avvisa(tutti ? t('Aggiorno tutti i moduli: l’istanza resta ferma per un po’') : t('Lavoro sui moduli: l’istanza si ferma un momento'));
      if (r.jobId) navigate({ to: '/orgs/$slug/jobs/$id', params: { slug, id: r.jobId } });
    },
  });

  return (
    <>
      <ConPermesso ha={permessi} serve="module.install">
        <Button variant="text" disabled={istanza.status !== 'running'} onClick={() => setApri(true)}>
          {t('Moduli…')}
        </Button>
      </ConPermesso>
      <Dialog
        open={apri}
        onOpenChange={(v) => !v && setApri(false)}
        title={t('Installa o aggiorna moduli')}
        description={t('Dentro Odoo, come da riga di comando: -i per installare, -u per aggiornare. L’istanza si ferma per il tempo che serve, con una copia del database prima.')}
        actions={
          <>
            <Button variant="text" onClick={() => setApri(false)}>{t('Annulla')}</Button>
            <Button
              variant="filled"
              icon={esegui.isPending ? <Anello /> : undefined}
              disabled={!pronto || esegui.isPending}
              onClick={() => esegui.mutate()}
            >
              {esegui.isPending ? t('Avvio…') : t('Esegui')}
            </Button>
          </>
        }
      >
        <div className="md-stack">
          {esegui.error && <Banner tone="error" title={t('Non partito')}>{(esegui.error as Error).message}</Banner>}
          <TextField
            label={t('Da installare')}
            placeholder="sale_management, note"
            spellCheck={false}
            value={installa}
            onChange={(e) => setInstalla(e.target.value)}
            help={t('Nomi tecnici dei moduli, separati da spazi o virgole. Il codice deve già essere sulla macchina: Odoo, Enterprise o un repository collegato.')}
          />
          {(nuovi.data?.modules ?? []).length > 0 && (
            <div className="md-stack" style={{ gap: 'var(--md-sys-spacing-2)' }}>
              <p className="md-body-small" style={{ margin: 0, color: 'var(--md-sys-color-on-surface-variant)' }}>
                {t('Nei repository collegati c’è il codice di questi moduli, e Odoo non li conosce ancora: finché non si installano non compaiono nemmeno fra le sue app.')}
              </p>
              <div className="md-stack__inline" style={{ flexWrap: 'wrap' }}>
                {(nuovi.data?.modules ?? []).map((m) => (
                  <Button
                    key={m.name}
                    variant="text"
                    disabled={daInstallare.includes(m.name)}
                    onClick={() => setInstalla((v) => (elenco(v).includes(m.name) ? v : `${v.trim()} ${m.name}`.trim()))}
                  >
                    {m.name}
                  </Button>
                ))}
              </div>
            </div>
          )}
          <TextField
            label={t('Da aggiornare')}
            placeholder="note"
            spellCheck={false}
            value={aggiorna}
            disabled={tutti}
            onChange={(e) => setAggiorna(e.target.value)}
            help={t('Solo moduli già installati: un nome che non lo è ferma tutto prima di cominciare.')}
          />
          <Checkbox
            checked={tutti}
            onCheckedChange={(v) => setTutti(v === true)}
            label={t('Aggiorna tutti i moduli installati (-u all)')}
          />
          {sbagliati.length > 0 && (
            <p className="md-body-small" style={{ margin: 0, color: 'var(--md-sys-color-error)' }}>
              {t('Non è un nome di modulo: {nomi}. Solo lettere, cifre e underscore.', { nomi: sbagliati.join(', ') })}
            </p>
          )}
          <p className="md-body-small" style={{ margin: 0, color: 'var(--md-sys-color-on-surface-variant)' }}>
            {t('Se qualcosa va storto l’istanza si riaccende com’era e il log dice dov’è la copia. Con «tutti» la durata dipende da quanti moduli e quanti dati ci sono: fallo prima su una copia di prova.')}
          </p>
        </div>
      </Dialog>
    </>
  );
}
